/**
 * BaseExplorer Configuration Factory
 * 
 * Implements builder pattern and factory methods for creating configuration-driven
 * BaseExplorer instances based on Entity Analysis complexity findings.
 */

import {
  BaseEntity,
  BaseExplorerConfig,
  EntityComplexity,
  EntityServiceAdapter,
  FieldTransformer,
  ExplorerFeatureConfig,
  PresentationConfig,
  ConfigBuilder,
  EntityConfigBuilder,
  ConfigValidation,
  ValidationError,
  ValidationWarning,
  EntityConfigFactory,
  ConfigFactoryOptions,
  ColumnConfig,
  PaginationFeatures,
  SortingFeatures,
  FilteringFeatures,
  SearchFeatures
} from '../types/baseExplorerConfig';

// Main Configuration Factory
export class BaseExplorerConfigFactory {
  /**
   * Create a configuration builder for fluent configuration creation
   */
  static createBuilder<T extends BaseEntity>(): ConfigBuilder<T> {
    return new BaseExplorerConfigBuilderImpl<T>();
  }

  /**
   * Create a pre-configured explorer based on complexity level
   */
  static createByComplexity<T extends BaseEntity>(
    options: ConfigFactoryOptions<T>
  ): BaseExplorerConfig<T> {
    const factory = new EntityConfigFactoryImpl<T>();
    return factory.createConfig(options);
  }

  /**
   * Validate a configuration
   */
  static validateConfig<T extends BaseEntity>(
    config: BaseExplorerConfig<T>
  ): ConfigValidation {
    const factory = new EntityConfigFactoryImpl<T>();
    return factory.validateConfig(config);
  }

  /**
   * Create default configurations for common complexity levels
   */
  static getDefaultFeatures(complexity: EntityComplexity): ExplorerFeatureConfig {
    switch (complexity) {
      case 'simple':
        return {
          pagination: { enabled: true, defaultPageSize: 50, pageSizeOptions: [25, 50, 100], showPageInfo: true },
          sorting: { enabled: true, multiColumn: false },
          filtering: { enabled: true, quickFilters: [], advancedFilters: false },
          searching: { enabled: true, placeholder: 'Search...', searchFields: [], debounceMs: 300 },
          editing: { enabled: true, mode: 'inline', allowCreate: true, allowUpdate: true, allowDelete: true },
          export: { enabled: true, formats: ['csv'], includeFilters: true }
        };

      case 'moderate':
        return {
          pagination: { enabled: true, defaultPageSize: 25, pageSizeOptions: [10, 25, 50], showPageInfo: true },
          sorting: { enabled: true, multiColumn: true },
          filtering: { enabled: true, quickFilters: [], advancedFilters: true },
          searching: { enabled: true, placeholder: 'Search entities...', searchFields: [], debounceMs: 500 },
          editing: { enabled: true, mode: 'modal', allowCreate: true, allowUpdate: true, allowDelete: false },
          export: { enabled: true, formats: ['csv', 'excel'], includeFilters: true }
        };

      case 'complex':
        return {
          pagination: { enabled: true, defaultPageSize: 10, pageSizeOptions: [5, 10, 25], showPageInfo: true },
          sorting: { enabled: true, multiColumn: true },
          filtering: { enabled: true, quickFilters: [], advancedFilters: true },
          searching: { enabled: true, placeholder: 'Search complex entities...', searchFields: [], debounceMs: 750 },
          editing: { enabled: false, mode: 'modal', allowCreate: false, allowUpdate: false, allowDelete: false },
          export: { enabled: true, formats: ['csv', 'excel', 'pdf'], includeFilters: true }
        };

      default:
        return BaseExplorerConfigFactory.getDefaultFeatures('moderate');
    }
  }
}

// Builder Implementation
class BaseExplorerConfigBuilderImpl<T extends BaseEntity> implements ConfigBuilder<T> {
  forEntity(entityName: string): EntityConfigBuilder<T> {
    return new EntityConfigBuilderImpl<T>(entityName);
  }
}

class EntityConfigBuilderImpl<T extends BaseEntity> implements EntityConfigBuilder<T> {
  private config: Partial<BaseExplorerConfig<T>> = {};

  constructor(entityName: string) {
    this.config.entityName = entityName;
  }

  withComplexity(level: EntityComplexity): EntityConfigBuilder<T> {
    this.config.complexityLevel = level;
    // Auto-apply default features based on complexity
    this.config.features = BaseExplorerConfigFactory.getDefaultFeatures(level);
    return this;
  }

  withFields(fields: (keyof T)[]): EntityConfigBuilder<T> {
    this.config.displayFields = fields;
    return this;
  }

  withServiceAdapter(adapter: EntityServiceAdapter<T>): EntityConfigBuilder<T> {
    this.config.serviceAdapter = adapter;
    return this;
  }

  withTransformer(field: keyof T, transformer: FieldTransformer): EntityConfigBuilder<T> {
    if (!this.config.fieldTransformers) {
      this.config.fieldTransformers = {} as Record<keyof T, FieldTransformer>;
    }
    this.config.fieldTransformers![field] = transformer;
    return this;
  }

  withFeatures(features: ExplorerFeatureConfig): EntityConfigBuilder<T> {
    this.config.features = { ...this.config.features, ...features };
    return this;
  }

  withPresentation(presentation: PresentationConfig): EntityConfigBuilder<T> {
    this.config.presentation = presentation;
    return this;
  }

  build(): BaseExplorerConfig<T> {
    // Validate required fields
    if (!this.config.entityName) {
      throw new Error('Entity name is required');
    }
    if (!this.config.complexityLevel) {
      throw new Error('Complexity level is required');
    }
    if (!this.config.displayFields || this.config.displayFields.length === 0) {
      throw new Error('Display fields are required');
    }
    if (!this.config.serviceAdapter) {
      throw new Error('Service adapter is required');
    }

    // Auto-generate presentation if not provided
    if (!this.config.presentation) {
      this.config.presentation = this.generateDefaultPresentation();
    }

    return this.config as BaseExplorerConfig<T>;
  }

  private generateDefaultPresentation(): PresentationConfig {
    const columns: ColumnConfig[] = (this.config.displayFields || []).map(field => ({
      field: String(field),
      title: this.fieldToTitle(String(field)),
      resizable: true,
      sortable: true,
      filterable: true,
      hidden: false
    }));

    return {
      title: this.config.entityName || 'Entity Explorer',
      columnConfig: columns,
      styling: {
        theme: 'default',
        striped: true,
        bordered: true,
        hoverable: true
      },
      layout: {
        minHeight: 400,
        responsive: true,
        showToolbar: true,
        showFooter: true
      }
    };
  }

  private fieldToTitle(field: string): string {
    return field
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  }
}

// Entity Configuration Factory Implementation
class EntityConfigFactoryImpl<T extends BaseEntity> implements EntityConfigFactory<T> {
  createConfig(options: ConfigFactoryOptions<T>): BaseExplorerConfig<T> {
    const baseConfig: BaseExplorerConfig<T> = {
      entityName: options.entityName,
      complexityLevel: options.complexityLevel,
      displayFields: this.getDefaultDisplayFields(options),
      serviceAdapter: options.serviceAdapter,
      features: BaseExplorerConfigFactory.getDefaultFeatures(options.complexityLevel),
      presentation: this.generateDefaultPresentation(options)
    };

    // Apply customizations
    if (options.customizations) {
      return { ...baseConfig, ...options.customizations };
    }

    return baseConfig;
  }

  validateConfig(config: BaseExplorerConfig<T>): ConfigValidation {
    const errors: ValidationError[] = [];
    const warnings: ValidationWarning[] = [];

    // Required field validation
    if (!config.entityName || config.entityName.trim() === '') {
      errors.push({
        field: 'entityName',
        message: 'Entity name is required and cannot be empty',
        severity: 'error'
      });
    }

    if (!config.complexityLevel) {
      errors.push({
        field: 'complexityLevel',
        message: 'Complexity level must be specified',
        severity: 'error'
      });
    }

    if (!config.displayFields || config.displayFields.length === 0) {
      errors.push({
        field: 'displayFields',
        message: 'At least one display field must be specified',
        severity: 'error'
      });
    }

    if (!config.serviceAdapter) {
      errors.push({
        field: 'serviceAdapter',
        message: 'Service adapter is required',
        severity: 'error'
      });
    }

    // Complexity-specific validation
    if (config.complexityLevel && config.displayFields) {
      const fieldCount = config.displayFields.length;
      
      switch (config.complexityLevel) {
        case 'simple':
          if (fieldCount > 10) {
            warnings.push({
              field: 'displayFields',
              message: `Simple entities typically show 3-8 fields, but ${fieldCount} fields are configured`,
              recommendation: 'Consider reducing the number of displayed fields for better user experience'
            });
          }
          break;

        case 'moderate':
          if (fieldCount > 15) {
            warnings.push({
              field: 'displayFields',
              message: `Moderate complexity entities typically show 5-12 fields, but ${fieldCount} fields are configured`,
              recommendation: 'Consider using field grouping or pagination to improve readability'
            });
          }
          break;

        case 'complex':
          if (fieldCount > 8) {
            warnings.push({
              field: 'displayFields',
              message: `Complex entities should show minimal fields (3-6), but ${fieldCount} fields are configured`,
              recommendation: 'Consider showing only the most essential fields and using drill-down for details'
            });
          }
          break;
      }
    }

    // Feature validation
    if (config.features?.editing?.enabled && config.complexityLevel === 'complex') {
      warnings.push({
        field: 'features.editing',
        message: 'Editing is enabled for complex entities',
        recommendation: 'Complex entities typically have editing disabled for safety'
      });
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  private getDefaultDisplayFields(options: ConfigFactoryOptions<T>): (keyof T)[] {
    // This would typically be determined by introspecting the entity type
    // For now, return a basic set that includes _id
    return ['_id' as keyof T];
  }

  private generateDefaultPresentation(options: ConfigFactoryOptions<T>): PresentationConfig {
    return {
      title: `${options.entityName} Explorer`,
      subtitle: `${options.complexityLevel} complexity entity management`,
      columnConfig: [],
      styling: {
        theme: 'default',
        striped: true,
        bordered: true,
        hoverable: true
      },
      layout: {
        minHeight: 400,
        responsive: true,
        showToolbar: true,
        showFooter: true
      }
    };
  }
}

// Convenience factory functions
export const createSimpleConfig = <T extends BaseEntity>(
  entityName: string,
  fields: (keyof T)[],
  serviceAdapter: EntityServiceAdapter<T>
): BaseExplorerConfig<T> => {
  return BaseExplorerConfigFactory.createBuilder<T>()
    .forEntity(entityName)
    .withComplexity('simple')
    .withFields(fields)
    .withServiceAdapter(serviceAdapter)
    .build();
};

export const createModerateConfig = <T extends BaseEntity>(
  entityName: string,
  fields: (keyof T)[],
  serviceAdapter: EntityServiceAdapter<T>
): BaseExplorerConfig<T> => {
  return BaseExplorerConfigFactory.createBuilder<T>()
    .forEntity(entityName)
    .withComplexity('moderate')
    .withFields(fields)
    .withServiceAdapter(serviceAdapter)
    .build();
};

export const createComplexConfig = <T extends BaseEntity>(
  entityName: string,
  fields: (keyof T)[],
  serviceAdapter: EntityServiceAdapter<T>
): BaseExplorerConfig<T> => {
  return BaseExplorerConfigFactory.createBuilder<T>()
    .forEntity(entityName)
    .withComplexity('complex')
    .withFields(fields)
    .withServiceAdapter(serviceAdapter)
    .build();
};
